import { isObservable, toJS } from 'mobx';
import expect from '../assert/expect';
import transform from 'lodash/transform';
import isEqual from 'lodash/isEqual';
import isObject from 'lodash/isObject';

export function copy(obj: any, isDeep = false, filterKeys = null) {
    const type = typeof obj;
    if (obj === null || type !== 'object') {
        return obj;
    }
    let copyObj;
    if (isObservable(obj)) {
        obj = toJS(obj);
    }
    if (Array.isArray(obj)) {
        // @ts-ignore
        copyObj = new obj.constructor(obj.length);
    } else {
        copyObj = Object.create(Object.getPrototypeOf(obj));
    }
    // eslint-disable-next-line no-restricted-syntax
    for (const key in obj) {
        // eslint-disable-next-line no-prototype-builtins
        if (!keyInFilter(key, filterKeys) && obj.hasOwnProperty(key)) {
            const element = obj[key];
            copyObj[key] = isDeep ? copy(element, isDeep, filterKeys) : element;
        }
    }
    return copyObj;
}

/**
 * 深拷贝
 * @param obj
 * @returns {*}
 */
export function deepCopy(obj) {
    return copy(obj, true, null);
}

function keyInFilter(key, filterKeys) {
    return Array.isArray(filterKeys) && filterKeys.indexOf(key) >= 0;
}

/**
 * 简单的deepMerge，请保证输入的都是对象...
 * @param {*} obj1
 * @param {*} obj2
 */
export function deepMerge(obj1, obj2) {
    if (expect(obj2).isUndefined()) return obj1;
    if (!expect(obj1).isObj() || !expect(obj2).isObj()) return obj2;

    const o = Object.keys(obj1).reduce((prev, key) => ({
        ...prev,
        [key]: deepMerge(obj1[key], obj2[key]),
    }), {});

    return Object.keys(obj2)
        .filter(key => !obj1[key])
        .reduce((prev, key) => ({
            ...prev,
            [key]: obj2[key],
        }), o);
}

export function basicCompare(objA, objB) {
    if (expect(objA).isNumber() && isNaN(objA)) {
        return expect(objB).isNumber() && isNaN(objB);
    }

    return objA === objB;
}

/**
 * 轻量级对象比较
 * @param objA
 * @param objB
 * @param excludeProp 要过滤不进行比较的属性,数组字符串，比如['id','value']。可以不传，比较全部
 */
export function objectCompareLite(objA, objB, excludeProp = null) {
    if (expect(objA).isBasic()) {
        return basicCompare(objA, objB);
    }

    if (expect(objA).isArray()) {
        if (!expect(objB).isArray()) return false;
        if (objA.length !== objB.length) return false;

        // 找到说明不相等
        return !objA.find((item, index) => !objectCompareLite(item, objB[index], excludeProp));
    }

    if (expect(objA).isObj()) {
        if (!expect(objB).isObj()) return false;
        // 除去忽略的props后，比较剩余的props个数是否相等
        objA = { ...objA };
        objB = { ...objB };

        // 去掉null和undefined
        Object.keys(objA).forEach((p) => {
            if (objA[p] === null || objA[p] === undefined) {
                delete objA[p];
            }
        });
        Object.keys(objB).forEach((p) => {
            if (objB[p] === null || objB[p] === undefined) {
                delete objB[p];
            }
        });
        const objAKeys = Object.keys(objA);
        const objBKeys = Object.keys(objB);

        let lenA = objAKeys.length;
        let lenB = objBKeys.length;

        if (excludeProp && (excludeProp as Array<any>).length > 0) {
            (excludeProp as Array<any>).forEach((p) => {
                const indexA = objAKeys.indexOf(p);
                if (indexA >= 0) {
                    lenA = lenA - 1;
                }

                const indexB = objBKeys.indexOf(p);
                if (indexB >= 0) {
                    lenB = lenB - 1;
                }
            });
        }
        if (lenA !== lenB) return false;

        // 找到说明不相等
        return !Object.keys(objA).find((key) => {
            if (excludeProp && (excludeProp as any).indexOf(key) >= 0) {
                return false;
            }
            return !objectCompareLite(objA[key], objB[key], excludeProp);
        });
    }

    return true;
}

export function difference(object, base) {
    function changes(object, base) {
        return transform(object, (result: any, value, key) => {
            if (!isEqual(value, base[key])) {
                result[key] = (isObject(value) && isObject(base[key])) ? changes(value, base[key]) : value;
            }
        });
    }
    return changes(object, base);
}

/**
 *
 * 可以对有循环引用的对象进行toJSON.
 * @param uiModel
 */
export function toJSON(obj) {
    let cache: any = [];
    const json = JSON.stringify(obj, (key, value) => {
        if (typeof value === 'object' && value !== null) {
            if (cache.indexOf(value) !== -1) {
                // Circular reference found, discard key
                return;
            }
            // Store value in our collection
            cache.push(value);
        }
        return value;
    });
    cache = null;
    return json;
}
